import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { LitElement, TemplateResult, html } from "lit";
import {
  customElement,
  property,
  queryAssignedNodes,
  state,
} from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { style } from "./assets/sidebar-navigation-item.generated-style";
import {
  ICON_HEIGHT,
  ICON_WIDTH,
  PREFIX_NESTING_CLASS,
} from "./sidebar-navigation-item.constants";
import { SlhSidebarNavigationType } from "./sidebar-navitaion-item.type";

/**
 * @CustomElements slh-sidebar-navigation-item
 *
 * @cssprop --slh-default-font-family - Font family.
 * @cssprop --slh-base-xs-font-size - Font size.
 * @cssprop --slh-base-bold-font-weight - Font weight.
 * @cssprop --slh-base-xxs-line-height - Line height.
 * @cssprop --slh-color-neutral-shade-00: Background color.
 * @cssprop --slh-color-neutral-shade-40: Count slot font color.
 * @cssprop --slh-color-neutral-shade-60: Icon color, nesting font color.
 * @cssprop --slh-color-neutral: First nesting font color.
 * @cssprop --slh-color-primary: Opened and active font color.
 * @cssprop --slh-color-primary-darken: Focused font color.
 * @cssprop --slh-color-outline - Focused element on tab border color.
 *
 * ** slots
 * @slot default - You should label text
 * @slot count - You should provide count
 * @slot subitems - You should provide slh-sidebar-navigation-item list.
 * */
@customElement("slh-sidebar-navigation-item")
export default class SlhSidebarNavigationItem extends LitElement {
  @queryAssignedNodes({ slot: "subitem" })
  protected subitemsSlotNodes: Array<HTMLElement> | undefined;

  @property({
    type: String,
    reflect: true,
  })
  link: string = "";

  @property({
    type: Boolean,
    reflect: true,
  })
  active: boolean = false;

  @state()
  openedSubitems: boolean = false;

  @state()
  showArrows: boolean = false;

  @state()
  depth: number = 1;

  static styles = style;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add("slh-sidebar-navigation-item-wrapper");
    this.setAttribute("role", "link");
    this.addEventListener("keydown", this.handleKeydown);
    this.calculateDepth();
  }

  disconnectedCallback() {
    this.removeEventListener("keydown", this.handleKeydown);

    super.disconnectedCallback();
  }

  protected firstUpdated(): void {
    if (this.active && this.parentNode?.nodeName === this.nodeName) {
      const slhSidebarNavigationItem = this
        .parentNode as SlhSidebarNavigationItem;
      slhSidebarNavigationItem.openSubItems();
    }
    this.sidebarNavigationHeading();
  }

  protected sidebarNavigationHeading() {
    if (this.active) {
      let parentElement = this.parentNode;
      while (parentElement?.nodeName === this.nodeName) {
        parentElement = parentElement.parentNode;
      }
      if (parentElement?.nodeName === "SLH-SIDEBAR-NAVIGATION") {
        const sidebarNavigation = parentElement as SlhSidebarNavigationType;
        sidebarNavigation.appendHeading(this);
      }
    }
  }

  private calculateDepth(): void {
    let { parentElement } = this;
    for (let i = 0; i < 2; i += 1) {
      if (parentElement?.nodeName === this.nodeName) {
        parentElement = parentElement.parentElement;
        this.depth += 1;
      }
    }

    this.classList.add(PREFIX_NESTING_CLASS + this.depth);
  }

  private openSubItems(e?: Event): void {
    if (this.subitemsSlotNodes!.length) {
      e?.preventDefault();
      this.openedSubitems = true;
      if (this.parentNode?.nodeName === this.nodeName) {
        const slhSidebarNavigationItem = this
          .parentNode as SlhSidebarNavigationItem;
        slhSidebarNavigationItem.openSubItems();
      }
    }
  }

  private onSlotChange(): void {
    this.showArrows = this.subitemsSlotNodes?.length !== 0;
  }

  closeSubitems() {
    this.openedSubitems = false;
    this.subitemsSlotNodes?.forEach((item) => {
      const sidebarItem = item as SlhSidebarNavigationItem;
      sidebarItem.closeSubitems();
    });
  }

  toggleSubitems(e?: Event): void {
    if (this.openedSubitems) {
      this.closeSubitems();
    } else {
      this.openSubItems(e);
    }
  }

  /**
   * @param {KeyboardEvent} event
   * w3.org keyboard support
   */
  protected handleKeydown(event: KeyboardEvent) {
    event.stopPropagation();
    const { code } = event;

    if (this.subitemsSlotNodes?.length === 0) {
      return false;
    }

    if (code === "ArrowDown") {
      this.openSubItems(event);
    } else if (code === "ArrowUp") {
      this.closeSubitems();
    } else if (code === "Enter" || code === "NumpadEnter" || code === "Space") {
      this.toggleSubitems();
    }
    return true;
  }

  render(): TemplateResult {
    return html`<a
      href="${this.link !== "" && this.subitemsSlotNodes?.length === 0
        ? this.link
        : // eslint-disable-next-line no-script-url
          "javascript:void(0)"}"
      class="slh-sidebarbar-navigation-item ${classMap({
        opened: this.openedSubitems,
        active: this.active,
        link: this.subitemsSlotNodes?.length === 0,
      })}"
    >
      <div class="item-details" @click="${this.toggleSubitems}">
        <span><slot></slot></span>
        <span class="count"><slot name="count"></slot></span>
        ${this.subitemsSlotNodes?.length !== 0
          ? html`<slh-icon
              type="${this.openedSubitems
                ? IconType.ICO_ARROW_OPEN_UP
                : IconType.ICO_ARROW_OPEN_DOWN}"
              class="${classMap({ hidden: !this.showArrows })}"
              width="${ICON_WIDTH}"
              height="${ICON_HEIGHT}"
            ></slh-icon>`
          : html``}
      </div>
      <div class="subitems">
        <slot @slotchange="${this.onSlotChange}" name="subitem"></slot>
      </div>
    </a>`;
  }
}
